// Politique de confidentialité — page statique publique

import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { LogoPreventionScop } from '../components/layout'
import { Separator } from '../components/ui/separator'

export default function ConfidentialitePage() {
  useEffect(() => { window.scrollTo(0, 0) }, [])
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <LogoPreventionScop className="h-8 w-auto" />
          </Link>
          <Link
            to="/"
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-4" />
            Retour à l'accueil
          </Link>
        </div>
      </header>

      {/* Contenu */}
      <main className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-2">Politique de confidentialité</h1>
        <p className="text-sm text-muted-foreground mb-8">Dernière mise à jour : mars 2026</p>

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Responsable de traitement</h2>
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>
              Le responsable du traitement des données personnelles collectées via le site{' '}
              <strong className="text-foreground">Diag SCOP Prévention</strong> (diagscopprevention.fr)
              est l'Organisme Professionnel de Prévention du Bâtiment et des Travaux Publics (OPPBTP),
              dont le siège est sis 25 avenue du Général Leclerc, 92660 Boulogne-Billancourt Cedex.
            </p>
            <p>
              Le traitement est placé sous la responsabilité de la Direction du numérique,
              représentée par Cyrille Goussard.
            </p>
          </div>
        </section>

        <Separator className="my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Données collectées</h2>
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>Dans le cadre de l'utilisation de Diag SCOP Prévention, les données suivantes peuvent être collectées :</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                <strong className="text-foreground">Données d'identification</strong> : nom, prénom, adresse email,
                identifiant BTP, transmis via le fournisseur d'identité PréventionBTP (OpenID Connect).
              </li>
              <li>
                <strong className="text-foreground">Données d'entreprise</strong> : raison sociale, SIRET,
                métiers sélectionnés.
              </li>
              <li>
                <strong className="text-foreground">Données de diagnostic</strong> : réponses au questionnaire,
                scores obtenus, niveau de casques, thèmes et solutions de prévention sélectionnés.
              </li>
              <li>
                <strong className="text-foreground">Données du plan d'actions</strong> : actions de prévention,
                responsables, échéances, statut d'avancement.
              </li>
              <li>
                <strong className="text-foreground">Données d'attestation</strong> : informations du dirigeant
                (genre, nom, prénom), type de certification, date de validation.
              </li>
              <li>
                <strong className="text-foreground">Données techniques</strong> : données de session,
                adresse IP (journaux serveur), pages consultées (via Google Analytics / Google Tag Manager).
              </li>
            </ul>
          </div>
        </section>

        <Separator className="my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Finalités du traitement</h2>
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>Les données personnelles sont collectées pour les finalités suivantes :</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Permettre la réalisation d'autodiagnostics sécurité et prévention.</li>
              <li>Générer des attestations de niveau de prévention.</li>
              <li>Assurer le suivi du plan d'actions prévention de l'entreprise.</li>
              <li>Permettre la mise à jour et le renouvellement des diagnostics.</li>
              <li>Produire des statistiques d'usage anonymisées pour l'amélioration du service.</li>
            </ul>
          </div>
        </section>

        <Separator className="my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Base légale</h2>
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>
              Le traitement est fondé sur l'<strong className="text-foreground">intérêt légitime</strong> de
              l'OPPBTP dans le cadre de sa mission de prévention des risques professionnels dans le secteur
              du bâtiment et des travaux publics (article 6.1.f du RGPD).
            </p>
          </div>
        </section>

        <Separator className="my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Destinataires des données</h2>
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>Les données collectées sont accessibles aux :</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>Collaborateurs de l'OPPBTP en charge de la gestion du service Diag SCOP Prévention.</li>
              <li>Utilisateurs de l'entreprise ayant accès au diagnostic concerné.</li>
            </ul>
            <p>
              Les données ne sont pas transmises à des tiers en dehors des prestataires techniques
              nécessaires au fonctionnement du service (hébergement, analytics).
            </p>
          </div>
        </section>

        <Separator className="my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Durée de conservation</h2>
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>
              Les données de diagnostic et d'attestation sont conservées pendant la durée de validité
              de l'attestation (1 ou 2 ans selon le niveau obtenu), prolongée d'un an pour permettre
              le renouvellement.
            </p>
            <p>
              Les données de session sont supprimées à l'expiration de la session (24 heures d'inactivité).
            </p>
            <p>
              Les journaux serveur sont conservés pendant 12 mois conformément aux obligations légales.
            </p>
          </div>
        </section>

        <Separator className="my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Cookies</h2>
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>Le site utilise les cookies suivants :</p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li>
                <strong className="text-foreground">Cookie de session</strong> (autodiag.sid) :
                strictement nécessaire au fonctionnement du service. Durée : session.
              </li>
              <li>
                <strong className="text-foreground">Google Analytics / Google Tag Manager</strong> :
                mesure d'audience et statistiques d'usage. Ces cookies peuvent être désactivés via les
                paramètres de votre navigateur.
              </li>
            </ul>
            <p>Aucun cookie publicitaire ou de ciblage n'est utilisé.</p>
          </div>
        </section>

        <Separator className="my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Hébergement</h2>
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>
              Les données sont hébergées en France par{' '}
              <strong className="text-foreground">Codein</strong>, Montpellier.
            </p>
          </div>
        </section>

        <Separator className="my-8" />

        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-4">Vos droits</h2>
          <div className="space-y-3 text-sm text-muted-foreground leading-relaxed">
            <p>
              Conformément au RGPD, vous disposez des droits suivants concernant vos données personnelles :
            </p>
            <ul className="list-disc pl-6 space-y-1.5">
              <li><strong className="text-foreground">Droit d'accès</strong> : obtenir la communication de vos données.</li>
              <li><strong className="text-foreground">Droit de rectification</strong> : corriger des données inexactes.</li>
              <li><strong className="text-foreground">Droit de suppression</strong> : demander l'effacement de vos données.</li>
              <li><strong className="text-foreground">Droit d'opposition</strong> : vous opposer au traitement de vos données.</li>
              <li><strong className="text-foreground">Droit à la portabilité</strong> : recevoir vos données dans un format structuré.</li>
              <li><strong className="text-foreground">Droit à la limitation</strong> : demander la limitation du traitement.</li>
            </ul>
            <p>
              Pour exercer ces droits, adressez votre demande par email à{' '}
              <a href="mailto:rgpd@oppbtp.fr" className="text-primary hover:underline">rgpd@oppbtp.fr</a>.
            </p>
            <p>
              En cas de difficulté, vous pouvez introduire une réclamation auprès de la CNIL :{' '}
              <a
                href="https://www.cnil.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                www.cnil.fr
              </a>.
            </p>
          </div>
        </section>
      </main>
    </div>
  )
}
